/**
 * Cookie Helper Utility
 * Handles cookie operations with security flags
 */

interface CookieOptions {
  days?: number;
  path?: string;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
}

/**
 * Set a cookie with optional parameters
 * @param name Cookie name
 * @param value Cookie value (will be JSON stringified if object)
 * @param options Cookie options (expires, path, secure, sameSite)
 */
export const setCookie = (name: string, value: any, options: CookieOptions = {}): void => {
  const {
    days = 1, // Default 1 day (24 hours)
    path = '/',
    secure = true, // Use Secure flag for HTTPS
    sameSite = 'Lax' // Use SameSite=Lax for CSRF protection
  } = options;

  // Calculate expiration date
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);

  // Serialize value
  const serializedValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
  
  // Build cookie string
  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(serializedValue)}`;
  cookieString += `; expires=${expires.toUTCString()}`;
  cookieString += `; path=${path}`;
  
  if (secure) {
    cookieString += '; Secure';
  }
  
  if (sameSite) {
    cookieString += `; SameSite=${sameSite}`;
  }

  document.cookie = cookieString;
};

/**
 * Get a cookie value by name
 * @param name Cookie name
 * @returns Parsed cookie value or null if not found
 */
export const getCookie = <T = any>(name: string): T | null => {
  const nameEQ = encodeURIComponent(name) + '=';
  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i].trim();
    
    if (cookie.indexOf(nameEQ) === 0) {
      const value = decodeURIComponent(cookie.substring(nameEQ.length));
      
      // Try to parse as JSON
      try {
        return JSON.parse(value) as T;
      } catch {
        // If not JSON, return as string
        return value as T;
      }
    }
  }
  
  return null;
};

/**
 * Delete a cookie by name
 * @param name Cookie name
 * @param path Cookie path (must match the path used when setting)
 */
export const deleteCookie = (name: string, path: string = '/'): void => {
  // Set cookie with past expiration date to delete it
  document.cookie = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}`;
};

/**
 * Check if a cookie exists
 * @param name Cookie name
 * @returns True if cookie exists, false otherwise
 */
export const hasCookie = (name: string): boolean => {
  return getCookie(name) !== null;
};
